<template>
  <div class="content-wrapper view-reconciliation p-0">
    <div class="content">
      <b-card>
        <h4><i class="fas fa-balance-scale"></i> กระทบยอด สปสช / ประกันสังคม</h4>
        <b-card no-body class="mt-1 mb-0 pb-0">
          <b-tabs v-model="payerIndex" card>
            <b-tab v-for="p in payers" :key="p.value" lazy>
              <template #title>
                {{ p.text }}
                <b-badge v-if="badges[p.value] !== undefined" pill :variant="payerIndex === payers.indexOf(p) ? 'warning' : 'light'" class="ml-1">
                  ค้างรับ {{ badges[p.value].toLocaleString("th-TH") }}{{ p.value === "NHSO" ? " visit" : "" }}
                </b-badge>
              </template>
              <PayerPanel
                :ref="`panel-${p.value}`"
                :payer="p.value"
                :initial-range="p.value === linkedPayer ? linkedRange : null"
                @totals="onTotals(p.value, $event)"
              />
            </b-tab>
            <template #tabs-end>
              <li class="nav-item ml-auto align-self-center pb-1">
                <ImportControls :payer="payer" @imported="onImported" />
              </li>
            </template>
          </b-tabs>
        </b-card>
      </b-card>
    </div>
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import PayerPanel from "@/components/reconciliation/PayerPanel.vue";
import ImportControls from "@/components/reconciliation/ImportControls.vue";
import Dialog from "@/components/modal/Dialog.vue";
import { formatThaiShortDate, initialPayer } from "@/utils/reconciliation";

// the payer tab last opened, per branch: many clinics only ever use one payer, so the page opens
// (and loads) just that one. Storage may be unavailable (private mode); then it opens on SSO.
const tabKey = (route) => `reconciliationPayer:${route.params.clinicUrl}/${route.params.branchUrl}`;
const readTab = (route) => {
  try {
    return window.localStorage.getItem(tabKey(route));
  } catch {
    return null;
  }
};
const saveTab = (route, payer) => {
  try {
    window.localStorage.setItem(tabKey(route), payer);
  } catch {
    // not remembered this time
  }
};

/**
 * One tab per payer (SSO receipt payments / NHSO visits). Each PayerPanel owns its filter, the
 * three status tiles and the table under them; the payer's import action sits at the end of the tab bar.
 * Tabs are lazy: only the open payer loads anything, and its panel reports the ค้างรับ count for its title.
 */
export default {
  name: "Reconciliation",
  components: { PayerPanel, ImportControls, Dialog },
  data() {
    // ?payer=&dateFrom=&dateTo= — the ลูกหนี้ report's "เปิดในหน้ากระทบยอด" link
    const { payer, dateFrom, dateTo } = this.$route.query;
    return {
      payerIndex: initialPayer(payer, readTab(this.$route)) === "NHSO" ? 1 : 0,
      linkedPayer: payer || null,
      linkedRange: payer ? { dateFrom: dateFrom || "", dateTo: dateTo || "" } : null,
      payers: [
        { value: "SSO", text: "ประกันสังคม" },
        { value: "NHSO", text: "สปสช." },
      ],
      // ค้างรับ count per payer for the tab titles, from each panel once it has been opened
      badges: {},
    };
  },
  computed: {
    payer() {
      return this.payers[this.payerIndex].value;
    },
  },
  watch: {
    payer(value) {
      saveTab(this.$route, value);
    },
  },
  methods: {
    onTotals(payer, totals) {
      if (totals) this.$set(this.badges, payer, totals.outstanding.count);
    },
    // Fired by ImportControls after either import path (NHSO upload or SSO
    // OCR review-and-commit) resolves — both return {batchId, matched, unmatched}.
    onImported(result = {}) {
      const { matched, unmatched } = result;
      const [panel] = this.$refs[`panel-${this.payer}`] || [];
      const widened = panel ? panel.onImported(result) : null;
      const rangeNote = widened
        ? ` · ปรับช่วงวันที่เป็น ${formatThaiShortDate(widened.dateFrom)} – ${formatThaiShortDate(widened.dateTo)} เพื่อแสดงรายการที่นำเข้า`
        : "";
      this.$refs.Dialog.showToast("success", `จับคู่ ${matched || 0} รายการ · ${unmatched || 0} ต้องตรวจสอบ${rangeNote}`);
    },
  },
};
</script>
